<script setup lang="ts">
import type {
  InitialData as HelpcenterSideBarBrowseFormInitialData,
  ValidData as HelpcenterSideBarBrowseFormValidData,
} from '@/components/forms/HelpcenterSideBarBrowseForm.vue';
import type { HelpcenterSidebarNavigationItem } from '@/stores/useNavigationsStore';

import { computed, reactive, toRaw, toRef, watch } from 'vue';
import { useRoute } from 'vue-router';
import _isEqual from 'lodash/isEqual';

import KBtn from '@/components/btns/KBtn.vue';
import HelpcenterSideBarArticleCard from '@/components/cards/HelpcenterSideBarArticleCard.vue';
import HelpcenterSideBarChat from '@/components/chat/HelpcenterSideBarChat.vue';
import HelpcenterSideBarBrowseForm from '@/components/forms/HelpcenterSideBarBrowseForm.vue';
import SmartHelpcenterSideBarHeader from '@/components/headers/SmartHelpcenterSideBarHeader.vue';
import SmartHelpcenterSideBarMenuList from '@/components/menuLists/SmartHelpcenterSideBarMenuList.vue';
import SmartHelpcenterSideBarTabs from '@/components/tabs/SmartHelpcenterSideBarTabs.vue';
import SmartHelpcenterSideBarVWindow from '@/components/vWindows/SmartHelpcenterSideBarVWindow.vue';
import HelpcenterSideBarItemWrapper from '@/components/wrappers/HelpcenterSideBarItemWrapper.vue';
import useHelpcenterSideBarData from '@/composables/data/useHelpcenterSideBarData';
import { useNavigationsStore } from '@/stores/useNavigationsStore';
import { useSidebarsStore } from '@/stores/useSidebarsStore';
import { useTabsStore } from '@/stores/useTabsStore';

const route = useRoute();
const tabsStore = useTabsStore();
const sidebarsStore = useSidebarsStore();
const navigationsStore = useNavigationsStore();

const lastNav = computed<HelpcenterSidebarNavigationItem | undefined>(() =>
  toRaw(
    navigationsStore.navigations.helpcenterSidebar[
      navigationsStore.navigations.helpcenterSidebar.length - 1
    ],
  ),
);

watch(
  () => route.name,
  function (newValue, oldValue) {
    if (newValue !== oldValue) {
      tabsStore.tabs.helpcenter = 'browse';
      navigationsStore.navigations.helpcenterSidebar = [];
    }
  },
);

const {
  isFetchingFaqArticleList,
  isFetchingTopSearchesArticleList,
  isFetchingRecentUpdatesArticleList,
  isFetchingSearchArticleList,
  isFetchingRouteArticleList,
  isFetchingArticle,
  isFetchingUpvoteArticle,
  isFetchingDownvoteArticle,
  fetchedFaqArticleList,
  fetchedTopSearchesArticleList,
  fetchedRecentUpdatesArticleList,
  fetchedSearchArticleList,
  fetchedRouteArticleList,
  fetchedArticle,
  upvoteArticle,
  downvoteArticle,
} = useHelpcenterSideBarData(
  toRef(sidebarsStore.sidebars, 'helpcenter'),
  toRef(tabsStore.tabs, 'helpcenter'),
  toRef(navigationsStore.navigations, 'helpcenterSidebar'),
  toRef(route, 'name'),
);

const initialBrowseData = computed<HelpcenterSideBarBrowseFormInitialData>(
  () => {
    return {
      search: '',
    };
  },
);

function handleBrowseSubmit(
  formData: HelpcenterSideBarBrowseFormValidData,
): void {
  const navigation: HelpcenterSidebarNavigationItem = {
    type: 'search',
    query: formData.search,
  };
  const isReplace = _isEqual(lastNav.value, navigation);
  if (isReplace) {
    navigationsStore.navigations.helpcenterSidebar =
      navigationsStore.navigations.helpcenterSidebar.slice(0, -1);
  }
  navigationsStore.navigations.helpcenterSidebar.push(navigation);
}

const limitNum = 4;

const limits = reactive({
  fetchedRouteArticleList: true,
  fetchedTopSearchesArticleList: true,
  fetchedRecentUpdatesArticleList: true,
  fetchedFaqArticleList: true,
});

function handleToggleLimitClick(limitKey: keyof typeof limits): void {
  limits[limitKey] = !limits[limitKey];
}
</script>

<template>
  <v-navigation-drawer
    :elevation="0"
    permanent
    :width="parseInt($style.helpcenterSideBarWidth)"
    location="right"
    :class="$style.smartHelpcenterSideBar"
    order="1"
  >
    <SmartHelpcenterSideBarHeader />
    <SmartHelpcenterSideBarTabs />
    <SmartHelpcenterSideBarVWindow :class="$style.windowItems">
      <template #default="{ tabValue, tabKey, tabIndex }">
        <div v-if="tabKey === 'browse'">
          <HelpcenterSideBarBrowseForm
            :initial-data="initialBrowseData"
            :loading="isFetchingSearchArticleList"
            @submit="handleBrowseSubmit"
          />

          <template v-if="lastNav?.type === 'search'">
            <HelpcenterSideBarItemWrapper
              :class="$style.itemWrapper"
              class="mt-4"
            >
              <h4 :class="$style.h4">
                Search results for "<i>{{ lastNav.query }}</i
                >"
              </h4>
              <SmartHelpcenterSideBarMenuList
                :items="fetchedSearchArticleList"
                :is-loading="isFetchingSearchArticleList"
              />
            </HelpcenterSideBarItemWrapper>
          </template>

          <template v-else-if="lastNav?.type === 'article'">
            <HelpcenterSideBarArticleCard
              class="mt-6"
              :article="fetchedArticle"
              :is-loading="isFetchingArticle"
              :is-fetching-upvote-article="isFetchingUpvoteArticle"
              :is-fetching-downvote-article="isFetchingDownvoteArticle"
              :upvote-article="upvoteArticle"
              :downvote-article="downvoteArticle"
            />
          </template>

          <template v-else-if="!lastNav">
            <HelpcenterSideBarItemWrapper
              :class="$style.itemWrapper"
              class="mt-4"
            >
              <h4 :class="$style.h4">Related articles</h4>
              <SmartHelpcenterSideBarMenuList
                :items="
                  limits.fetchedRouteArticleList
                    ? fetchedRouteArticleList?.slice(0, limitNum)
                    : fetchedRouteArticleList
                "
                :is-loading="isFetchingRouteArticleList"
              />
              <KBtn
                v-if="Number(fetchedRouteArticleList?.length) > limitNum"
                :class="$style.toggleLimitBtn"
                size="sm"
                variant="outlined"
                @click="handleToggleLimitClick('fetchedRouteArticleList')"
              >
                Show
                {{ limits.fetchedRouteArticleList ? 'more' : 'less' }}
              </KBtn>
            </HelpcenterSideBarItemWrapper>

            <HelpcenterSideBarItemWrapper
              :class="$style.itemWrapper"
              class="mt-4"
            >
              <h4 :class="$style.h4">Top searches</h4>
              <SmartHelpcenterSideBarMenuList
                :items="
                  limits.fetchedTopSearchesArticleList
                    ? fetchedTopSearchesArticleList?.slice(0, limitNum)
                    : fetchedTopSearchesArticleList
                "
                :is-loading="isFetchingTopSearchesArticleList"
              />
              <KBtn
                v-if="Number(fetchedTopSearchesArticleList?.length) > limitNum"
                :class="$style.toggleLimitBtn"
                size="sm"
                variant="outlined"
                @click="handleToggleLimitClick('fetchedTopSearchesArticleList')"
              >
                Show
                {{ limits.fetchedTopSearchesArticleList ? 'more' : 'less' }}
              </KBtn>
            </HelpcenterSideBarItemWrapper>

            <HelpcenterSideBarItemWrapper
              :class="$style.itemWrapper"
              class="mt-4"
            >
              <h4 :class="$style.h4">Recent updates and release notes</h4>
              <SmartHelpcenterSideBarMenuList
                :items="
                  limits.fetchedRecentUpdatesArticleList
                    ? fetchedRecentUpdatesArticleList?.slice(0, limitNum)
                    : fetchedRecentUpdatesArticleList
                "
                :is-loading="isFetchingRecentUpdatesArticleList"
              />
              <KBtn
                v-if="
                  Number(fetchedRecentUpdatesArticleList?.length) > limitNum
                "
                :class="$style.toggleLimitBtn"
                size="sm"
                variant="outlined"
                @click="
                  handleToggleLimitClick('fetchedRecentUpdatesArticleList')
                "
              >
                Show
                {{ limits.fetchedRecentUpdatesArticleList ? 'more' : 'less' }}
              </KBtn>
            </HelpcenterSideBarItemWrapper>

            <HelpcenterSideBarItemWrapper
              :class="$style.itemWrapper"
              class="mt-4"
            >
              <h4 :class="$style.h4">FAQ</h4>
              <SmartHelpcenterSideBarMenuList
                :items="
                  limits.fetchedFaqArticleList
                    ? fetchedFaqArticleList?.slice(0, limitNum)
                    : fetchedFaqArticleList
                "
                :is-loading="isFetchingFaqArticleList"
              />
              <KBtn
                v-if="Number(fetchedFaqArticleList?.length) > limitNum"
                :class="$style.toggleLimitBtn"
                size="sm"
                variant="outlined"
                @click="handleToggleLimitClick('fetchedFaqArticleList')"
              >
                Show
                {{ limits.fetchedFaqArticleList ? 'more' : 'less' }}
              </KBtn>
            </HelpcenterSideBarItemWrapper>
          </template>
        </div>
        <div v-else-if="tabKey === 'contact'">
          <HelpcenterSideBarChat
            v-if="
              /* unmounting */
              tabKey === tabsStore.tabs.helpcenter &&
              sidebarsStore.sidebars.helpcenter
            "
          />
        </div>
      </template>
    </SmartHelpcenterSideBarVWindow>
  </v-navigation-drawer>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use 'sass:math';
@use '@/styles/colors';
@use '@/styles/variables';

:export {
  helpcenterSideBarWidth: variables.$helpcenterSideBarWidth;
}

.smartHelpcenterSideBar {
  border-left: 1px solid colors.$color-brand-100 !important;
}
  .windowItems {
    padding: variables.$helpcenterSideBarPadding;
  }
    .itemWrapper {
      font-size: 14px;
      line-height: 1.4;
    }
      .h4 {
        margin: 4px 0 10px 0;
        font-weight: 600;
      }
      .toggleLimitBtn {
        margin-top: 8px;
      }
</style>
